import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, KeyRound, ArrowLeft, Copy, CheckCircle2 } from 'lucide-react';

function formatErr(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || JSON.stringify(d)).join('; ');
  return error?.message || fallback;
}

/**
 * Login second-factor challenge. Shown after a correct password when the
 * account has MFA enabled. No full session exists until this succeeds.
 */
export function MfaChallengePanel({ mfaToken, onVerified, onCancel }) {
  const { verifyMfa } = useAuth();
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await verifyMfa(mfaToken, code.trim());
      onVerified(user);
    } catch (error) {
      toast.error(formatErr(error, 'Verification failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-[520px]" data-testid="mfa-challenge-panel">
      <button
        type="button"
        onClick={onCancel}
        className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-primary transition-colors mb-8"
        data-testid="mfa-back"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to sign in
      </button>

      <div className="mb-7">
        <div className="inline-flex items-center gap-2 rounded-md border border-[#DDE7E5] bg-white px-3 py-2 text-sm font-medium text-[#0F5957] shadow-sm">
          <ShieldCheck className="h-4 w-4" />
          Two-factor authentication
        </div>
        <h2 className="font-heading mt-5 text-3xl font-bold text-slate-950">Enter your verification code</h2>
        <p className="mt-3 text-base text-slate-600">
          {useRecovery
            ? 'Enter one of your one-time recovery codes to finish signing in.'
            : 'Open your authenticator app and enter the current 6-digit code for this account.'}
        </p>
      </div>

      <form onSubmit={submit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="mfa-code" className="text-slate-800">
            {useRecovery ? 'Recovery code' : 'Authentication code'}
          </Label>
          <div className="relative">
            <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
            <Input
              id="mfa-code"
              inputMode={useRecovery ? 'text' : 'numeric'}
              autoComplete="one-time-code"
              autoFocus
              placeholder={useRecovery ? 'xxxx-xxxx-xxxx' : '123456'}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="pl-12 h-12 rounded-md border-[#D9E2E1] bg-white tracking-widest focus:border-primary focus:ring-primary/20"
              required
              data-testid="mfa-code-input"
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={loading || !code.trim()}
          className="w-full h-12 rounded-md bg-[#0F6F6A] text-white font-semibold hover:bg-[#0B5E5A]"
          data-testid="mfa-verify-submit"
        >
          {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Verifying...</> : 'Verify and sign in'}
        </Button>
      </form>

      <button
        type="button"
        onClick={() => { setUseRecovery((v) => !v); setCode(''); }}
        className="mt-6 text-sm font-medium text-[#0F6F6A] hover:underline"
        data-testid="mfa-toggle-recovery"
      >
        {useRecovery ? 'Use an authenticator code instead' : "Can't access your authenticator? Use a recovery code"}
      </button>
    </div>
  );
}

/**
 * Mandatory enrolment during login (only reached when a role is under a
 * mandatory MFA policy and has not enrolled yet).
 */
export function MfaEnrollPanel({ mfaToken, onEnrolled, onCancel }) {
  const { beginMfaEnrollment, completeMfaEnrollment } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [pendingUser, setPendingUser] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await beginMfaEnrollment(mfaToken);
        if (active) setSetup(data);
      } catch (error) {
        toast.error(formatErr(error, 'Could not start MFA setup'));
        onCancel?.();
      }
    })();
    return () => { active = false; };
  }, [mfaToken]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { user, recoveryCodes: codes } = await completeMfaEnrollment(mfaToken, code.trim());
      setPendingUser(user);
      setRecoveryCodes(codes);
    } catch (error) {
      toast.error(formatErr(error, 'Verification failed'));
    } finally {
      setLoading(false);
    }
  };

  if (recoveryCodes) {
    return (
      <RecoveryCodesView
        codes={recoveryCodes}
        onDone={() => onEnrolled(pendingUser)}
        doneLabel="Continue to workspace"
      />
    );
  }

  return (
    <div className="w-full max-w-[520px]" data-testid="mfa-enroll-panel">
      <div className="mb-6">
        <div className="inline-flex items-center gap-2 rounded-md border border-[#DDE7E5] bg-white px-3 py-2 text-sm font-medium text-[#0F5957] shadow-sm">
          <ShieldCheck className="h-4 w-4" />
          Set up two-factor authentication
        </div>
        <h2 className="font-heading mt-5 text-3xl font-bold text-slate-950">Protect your admin account</h2>
        <p className="mt-3 text-base text-slate-600">
          Your role now requires two-factor authentication. Scan the QR code with an
          authenticator app (Google Authenticator, Microsoft Authenticator, 1Password),
          then enter the 6-digit code to finish.
        </p>
      </div>

      {!setup ? (
        <div className="flex items-center gap-2 py-8 text-slate-600">
          <Loader2 className="h-5 w-5 animate-spin" /> Preparing your setup…
        </div>
      ) : (
        <MfaSetupBody setup={setup} code={code} setCode={setCode} loading={loading} onSubmit={submit} submitLabel="Verify and enable" />
      )}
      <button type="button" onClick={onCancel} className="mt-6 text-sm font-medium text-slate-600 hover:text-primary">
        Cancel and sign in later
      </button>
    </div>
  );
}

/** Shared QR + secret + code entry body, reused by login enrol and settings setup. */
export function MfaSetupBody({ setup, code, setCode, loading, onSubmit, submitLabel = 'Verify and enable' }) {
  const [copied, setCopied] = useState(false);
  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(setup.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard may be blocked */ }
  };
  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid="mfa-setup-body">
      <div className="flex flex-col items-center gap-4 rounded-xl border border-[#DDE7E5] bg-white p-5 sm:flex-row sm:items-start">
        <img
          src={setup.qr_code}
          alt="MFA QR code"
          className="h-40 w-40 shrink-0 rounded-lg border border-[#E4E8EB]"
          data-testid="mfa-qr"
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-800">Can’t scan?</p>
          <p className="text-xs text-slate-600">Enter this setup key manually in your app:</p>
          <div className="mt-2 flex items-center gap-2">
            <code className="truncate rounded bg-[#F1F5F4] px-2 py-1 text-xs text-slate-800" data-testid="mfa-secret">
              {setup.secret}
            </code>
            <button type="button" onClick={copySecret} className="text-slate-500 hover:text-primary" aria-label="Copy setup key">
              {copied ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="mfa-setup-code" className="text-slate-800">6-digit code from your app</Label>
        <div className="relative">
          <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
          <Input
            id="mfa-setup-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="pl-12 h-12 rounded-md border-[#D9E2E1] bg-white tracking-widest focus:border-primary focus:ring-primary/20"
            required
            data-testid="mfa-setup-code-input"
          />
        </div>
      </div>

      <Button
        type="submit"
        disabled={loading || !code.trim()}
        className="w-full h-12 rounded-md bg-[#0F6F6A] text-white font-semibold hover:bg-[#0B5E5A]"
        data-testid="mfa-setup-verify"
      >
        {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Verifying...</> : submitLabel}
      </Button>
    </form>
  );
}

/** One-time recovery codes display. */
export function RecoveryCodesView({ codes, onDone, doneLabel = 'Done' }) {
  const copyAll = async () => {
    try { await navigator.clipboard.writeText(codes.join('\n')); toast.success('Recovery codes copied'); }
    catch { /* clipboard may be blocked */ }
  };
  return (
    <div className="w-full max-w-[520px]" data-testid="mfa-recovery-codes">
      <div className="mb-5">
        <div className="inline-flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-800">
          <CheckCircle2 className="h-4 w-4" />
          Two-factor authentication enabled
        </div>
        <h2 className="font-heading mt-5 text-2xl font-bold text-slate-950">Save your recovery codes</h2>
        <p className="mt-2 text-sm text-slate-600">
          Store these somewhere safe. Each code works once if you lose your authenticator.
          They will not be shown again.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-2 rounded-xl border border-[#DDE7E5] bg-white p-4 font-mono text-sm text-slate-800">
        {codes.map((c) => (
          <div key={c} className="rounded bg-[#F1F5F4] px-2 py-1 text-center">{c}</div>
        ))}
      </div>
      <div className="mt-4 flex gap-3">
        <Button type="button" variant="outline" onClick={copyAll} className="rounded-md">
          <Copy className="mr-2 h-4 w-4" /> Copy codes
        </Button>
        <Button type="button" onClick={onDone} className="flex-1 rounded-md bg-[#0F6F6A] text-white hover:bg-[#0B5E5A]" data-testid="mfa-recovery-done">
          {doneLabel}
        </Button>
      </div>
    </div>
  );
}
